import * as tslib_1 from "tslib";
import * as undoActions from 'owa-mail-undo';
import clearTableViewLastEmptiedTime from 'owa-mail-triage-action/lib/actions/clearTableViewLastEmptiedTime';
import datapoints from './datapoints';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import getDeleteAllConfirmText from './helpers/getDeleteAllConfirmText';
import getItemIdsFromConversations from 'owa-mail-list-store/lib/utils/getItemIdsFromConversations';
import getItemidsFromMessages from 'owa-mail-list-store/lib/utils/getItemidsFromMessages';
import getSelectedFolder from 'owa-mail-store/lib/utils/getSelectedFolder';
import { isUnderDeletedItemsFolderFromAnyMailbox } from 'owa-mail-store';
import strings from 'owa-strings/owa-mail-strings';
import { action } from 'satcheljs/lib/legacy';
import { actionNames } from 'owa-mail-store-actions';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import { emptyFolderService } from '../services/emptyFolderService';
import folderStore from 'owa-folders';
import { format } from 'owa-strings';
import { getListViewTypeForFolder, listViewStore } from 'owa-mail-list-store';
import { getServiceErrorCallback } from 'owa-service-error/lib/initializeServiceErrorHandler';
import { handleServiceError } from 'owa-service-error';
import { isControlFeatureEnabled } from 'owa-controls-feature-flags';
import { isMailFeatureEnabled } from 'owa-mail-feature-flags';
import { lazyOnBulkActionSubmitted } from 'owa-bulk-action-store';
import { logUsage } from 'owa-analytics';
import { raiseAction } from 'satcheljs/lib/legacy/stitch';
import getEmptyFolderHelperProperties from './helpers/emptyFolderHelper';
/**
 * Get the type of items to delete based on view filter
 * @param viewFilter the view filter
 * @returns the text which indicates type of items to delete based on the view filter
 */
function getItemsTypeText(viewFilter) {
    switch (viewFilter) {
        case 'Unread':
            return strings.allUnreadItems;
        case 'Flagged':
            return strings.allFlaggedItems;
        case 'ToOrCcMe':
            return strings.allToMeItems;
        case 'All':
        default:
            return strings.allItems;
    }
}
/**
 * Get the folder scope text
 * @param focusedViewFilter the focused view filter
 * @param folderDisplayName the folder display name
 * @returns the folder scope text, e.g Focused Inbox, Other Inbox, Sent Items, etc.
 */
function getFolderScopeText(focusedViewFilter, folderDisplayName) {
    var focusedViewFilterText = '';
    switch (focusedViewFilter) {
        case 0 /* Focused */:
            focusedViewFilterText = strings.focusedViewFilterText + ' ';
            break;
        case 1 /* Other */:
            focusedViewFilterText = strings.otherViewFilterText + ' ';
            break;
    }
    return focusedViewFilterText + folderDisplayName;
}
function bulkActionErrorHandler(error, friendlyError) {
    if (isMailFeatureEnabled('tri-bulkActionNotification')) {
        if (error.response && error.response.status != 503) {
            var callback = getServiceErrorCallback();
            if (friendlyError && callback) {
                callback(friendlyError);
            }
        }
    }
}
/**
 * Empty folder without the confirm dialog
 * @param folder the folder to be emptied
 * @param disposalType the disposalType
 * @param clientLastSyncTime client last sync time used as a watermark
 * @param rowKeysToExclude item ids to exclude from empty folder operation
 * @param tableViewId id of the tableView where empty operation is performed.
 * @param deletedItemsFolderId id of the deleted items folder for the mbx to which the folder being emptied belongs
 * @param shouldSupportBulkActions flag indicating whether the bulk actions is being supported or not
 */
var emptyFolderWithoutConfirmation = action('emptyFolderWithoutConfirmation')(function emptyFolderWithoutConfirmation(folder, disposalType, deleteSubFolders, clientLastSyncTime, rowKeysToExclude, tableViewId, deletedItemsFolderId, shouldSupportBulkActions) {
    // Collect itemIds based on listViewType
    var itemIdsToExclude = [];
    if (rowKeysToExclude.length > 0) {
        var tableView = listViewStore.tableViews.get(tableViewId);
        switch (tableView.tableQuery.listViewType) {
            case 0 /* Conversation */:
                itemIdsToExclude = getItemIdsFromConversations(rowKeysToExclude, tableViewId);
                break;
            case 1 /* Message */:
                itemIdsToExclude = getItemidsFromMessages(rowKeysToExclude, tableView);
                break;
        }
    }
    // TODO: VSO 11909: Handle undo for destructive mail actions
    var actionResponse = handleServiceError('emptyFolderWithoutConfirmation', format(strings.errorEmptyingFolder, folder.DisplayName), function () {
        return emptyFolderService(folder, disposalType, deleteSubFolders, clientLastSyncTime, itemIdsToExclude, shouldSupportBulkActions);
    }, undefined, bulkActionErrorHandler /* Do not show grey pill */);
    raiseAction(actionNames.EmptyFolderStoreUpdate)(folder, clientLastSyncTime, deletedItemsFolderId, rowKeysToExclude);
    return actionResponse;
});
var emptyFolderActionName = 'emptyFolderAction';
export { emptyFolderActionName };
/**
 * Delete all conversations or items in the specified folder
 * @param folder the folder to be emptied
 * @param focusedViewFilter the focused view filter
 * @param viewFilter the view filter
 * @param isExplicitSoftDelete flag to indicate whether disposal type is soft delete
 * @param clientLastSyncTime client last sync time used as a watermark
 * @param rowKeysToExclude row keyss to exclude from empty folder operation
 */
export default action(emptyFolderActionName)(function emptyFolder(folder, focusedViewFilter, viewFilter, isExplicitSoftDelete, actionSource, rowKeysToExclude, clientLastSyncTime, tableViewId) {
    var _this = this;
    var confirmEmptyFolderSubtext;
    var disposalType;
    var deleteSubFolders;
    undoActions.clearLastUndoableAction();
    var folderScopeText = getFolderScopeText(focusedViewFilter, folder.DisplayName);
    var itemsTypeText = getItemsTypeText(viewFilter);
    var exclusionListCount = rowKeysToExclude ? rowKeysToExclude.length : 0;
    var confirmEmptyFolderSubTextString;
    var folderToBeEmptied = folder;
    // If a tableViewId is specified we are emptying the table, in which case we want get the actual folder
    // to get the emptyFolder helper properties
    if (tableViewId) {
        var tableView = listViewStore.tableViews.get(tableViewId);
        folderToBeEmptied = folderStore.folderTable.get(tableView.tableQuery.folderId);
    }
    var _a = getEmptyFolderHelperProperties(folderToBeEmptied), isFolderDeletedItemsFolder = _a.isFolderDeletedItemsFolder, deletedItemsFolderId = _a.deletedItemsFolderId, shouldSupportBulkActions = _a.shouldSupportBulkActions, treeType = _a.treeType;
    if (folder.DistinguishedFolderId == 'recoverableitemsdeletions') {
        confirmEmptyFolderSubTextString = strings.confirmPermanentEmptyFolderSubText;
        deleteSubFolders = false;
        disposalType = 'HardDelete';
    }
    else if (isFolderDeletedItemsFolder || folder.DistinguishedFolderId == 'junkemail') {
        confirmEmptyFolderSubTextString = strings.confirmPermanentEmptyFolderSubText;
        deleteSubFolders = !isControlFeatureEnabled('doc-consumerUrl');
        // This is to ensure the contract on server that clientLastSyncTime and deleteSubFolders cannot be present at the same time
        clientLastSyncTime = deleteSubFolders ? null : clientLastSyncTime;
        disposalType = 'SoftDelete';
    }
    else if (isUnderDeletedItemsFolderFromAnyMailbox(folder)) {
        confirmEmptyFolderSubTextString =
            strings.confirmEmptyFolderForFolderUnderDeletedItemsSubText;
        deleteSubFolders = false;
        disposalType = 'SoftDelete';
    }
    else {
        if (isExplicitSoftDelete) {
            confirmEmptyFolderSubTextString = strings.confirmPermanentEmptyFolderSubText;
            disposalType = 'SoftDelete';
        }
        else {
            confirmEmptyFolderSubTextString = strings.confirmEmptyFolderSubText;
            disposalType = 'MoveToDeletedItems';
        }
        deleteSubFolders = false;
    }
    confirmEmptyFolderSubtext = format(confirmEmptyFolderSubTextString, itemsTypeText, folderScopeText);
    return confirm(getDeleteAllConfirmText(actionSource, getListViewTypeForFolder(folder.FolderId.Id) /* listViewType */, folder.TotalCount - exclusionListCount), confirmEmptyFolderSubtext, false /* resolveImmediately */, {
        // Wait until dialog is dismissed to avoid focus interruption
        delayCallbackAfterAnimation: true,
        okText: strings.confirmEmptyFolderText,
    }).then(function (response) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
        var tableView_1;
        return tslib_1.__generator(this, function (_a) {
            if (response === DialogResponse.ok) {
                if (shouldSupportBulkActions) {
                    lazyOnBulkActionSubmitted.importAndExecute(folder.FolderId.Id, 'EmptyFolder' /* scenario */, null /* customData */, folder.TotalCount - exclusionListCount /* totalCount */);
                }
                // TODO : Add check for shared folders during implementation
                logUsage(datapoints.emptyFolderDatapoint.datapoint.name, [
                    actionSource,
                    folder.TotalCount,
                    folderIdToName(folder.FolderId.Id),
                    folderIdToName(getSelectedFolder().FolderId.Id),
                    disposalType,
                    treeType,
                ]);
                tableView_1 = listViewStore.tableViews.get(tableViewId);
                return [2 /*return*/, emptyFolderWithoutConfirmation(folder, disposalType, deleteSubFolders, clientLastSyncTime, rowKeysToExclude, tableViewId, deletedItemsFolderId, shouldSupportBulkActions)
                        .then(function () {
                        tableView_1 && clearTableViewLastEmptiedTime(tableView_1);
                    })
                        .catch(function () {
                        tableView_1 && clearTableViewLastEmptiedTime(tableView_1);
                    })];
            }
            return [2 /*return*/];
        });
    }); });
});
//# sourceMappingURL=emptyFolder.js.map